import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import PeajeReportCards from "./PeajeReportCards";
import { getConcessionPeajes } from "./getConcessionPeajes";
import { getConciliationDetailByIdAndConcession } from "modules/finops/conciliaciones/services/financieraService";

// Llaves de conciliación distintas que traen los pasos de la fila.
export function llavesDeConciliacion(detail) {
  const pasos = Array.isArray(detail?.data) ? detail.data : [];
  return [
    ...new Set(pasos.map((paso) => String(paso?.llave_conciliacion || "").trim()).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b, "es"));
}

// more[peaje][llave de conciliación] = { steps, value }
export function summarizePeajes(rows) {
  const more = Object.create(null);
  let hasSteps = false,
    hasValue = false;
  let steps = 0,
    cents = 0;
  rows.forEach((row) => {
    const tienePasos = row.steps !== "" && row.steps != null;
    const tieneValor = row.value !== "" && row.value != null;
    if (!tienePasos && !tieneValor) return;
    const llave = String(row.llave || "").trim();
    if (!llave) throw new Error("Falta la llave de conciliación del desglose.");
    if (tienePasos && (!/^\d+$/.test(row.steps) || !Number.isSafeInteger(Number(row.steps))))
      throw new Error(`Completa la cantidad de pasos de ${row.peaje} en ${llave}.`);
    if (!/^\d+(\.\d{1,2})?$/.test(row.value))
      throw new Error(`Completa el valor de ${row.peaje} en ${llave}, con máximo dos decimales.`);
    const amount = Number(row.value);
    const rowCents = Math.round(amount * 100);
    if (!Number.isSafeInteger(rowCents))
      throw new Error(`El valor de ${row.peaje} en ${llave} es demasiado grande.`);
    if (tienePasos) steps += Number(row.steps);
    hasSteps = hasSteps || tienePasos;
    hasValue = true;
    cents += rowCents;
    if (!more[row.peaje]) more[row.peaje] = Object.create(null);
    more[row.peaje][llave] = { ...(tienePasos ? { steps: Number(row.steps) } : {}), value: amount };
  });
  if (!Number.isSafeInteger(steps) || !Number.isSafeInteger(cents))
    throw new Error("Los totales exceden el valor permitido.");
  return {
    more,
    steps: hasSteps ? steps : undefined,
    value_concil: hasValue ? (cents / 100).toFixed(2) : undefined,
  };
}
const formatted = (value) => {
  if (value === "") return "";
  const [integer, decimal] = value.split(".");
  return (
    integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + (decimal !== undefined ? `,${decimal}` : "")
  );
};
const agruparPorLlave = (rows) => {
  const grupos = [];
  rows.forEach((row, index) => {
    let grupo = grupos[grupos.length - 1];
    if (!grupo || grupo.llave !== row.llave) {
      grupo = { llave: row.llave, filas: [] };
      grupos.push(grupo);
    }
    grupo.filas.push({ row, index });
  });
  return grupos;
};
export default function ConcessionPeajes({
  code,
  conciliationId,
  detailReference,
  concesion,
  keyConcil,
  rows,
  onChange,
  disabled,
  porPeaje = false,
  onEnviarPeaje,
  reportados = {},
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    onChange([]);
    Promise.all([
      getConcessionPeajes(code, controller.signal),
      keyConcil || detailReference || conciliationId
        ? getConciliationDetailByIdAndConcession(
            keyConcil || detailReference || conciliationId,
            concesion,
            {},
            controller.signal
          )
        : Promise.resolve({ success: true, data: [] }),
    ])
      .then(([names, detail]) => {
        if (controller.signal.aborted) return;
        if (!detail.success)
          throw new Error(detail.message || "No se pudieron cargar las llaves de conciliación.");
        const llaves = llavesDeConciliacion(detail);
        const grupos = (llaves.length ? llaves : [keyConcil])
          .map((value) => String(value || "").trim())
          .filter(Boolean);
        if (!grupos.length) {
          setError("No se encontraron llaves de conciliación para esta fila.");
          return;
        }
        onChange(
          grupos.flatMap((llave) => names.map((peaje) => ({ llave, peaje, steps: "", value: "" })))
        );
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(err.response?.data?.message || err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [code, conciliationId, detailReference, concesion, keyConcil, onChange, revision]);
  const change = (index, field, text) => {
    const value = text.replace(/\./g, "").replace(",", ".");
    if (!(field === "steps" ? /^\d*$/ : /^\d*(\.\d{0,2})?$/).test(value)) return;
    onChange((current) =>
      current.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };
  let total;
  try {
    total = summarizePeajes(rows);
  } catch (_) {
    total = null;
  }
  return (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{ color: "#475569" }}>
        {porPeaje
          ? "Completa los pasos, el valor y el Excel del peaje a tu cargo. Puedes enviar cada peaje por separado; los ya enviados no se pueden editar."
          : "Los campos son opcionales. Si ingresas pasos de un peaje, completa también su valor total. Los totales se calculan con la información ingresada."}
      </Typography>
      {loading && (
        <Box role="status">
          <CircularProgress size={20} />{" "}
          <Typography component="span" variant="body2">
            Cargando peajes y llaves de conciliación…
          </Typography>
        </Box>
      )}
      {error && (
        <Alert
          severity="error"
          action={
            <Button disabled={disabled} onClick={() => setRevision((value) => value + 1)}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {porPeaje && !loading && !error && (
        <PeajeReportCards
          rows={rows}
          reportados={reportados}
          disabled={disabled}
          onChange={onChange}
          onEnviar={onEnviarPeaje}
        />
      )}
      {!porPeaje &&
        agruparPorLlave(rows).map((grupo) => (
          <Box
            key={grupo.llave}
            sx={{ border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden" }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                bgcolor: "background.default",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "text.primary",
                  overflowWrap: "anywhere",
                }}
              >
                {grupo.llave}
              </Typography>
            </Box>
            <Stack spacing={2} sx={{ p: 2 }}>
              {grupo.filas.map(({ row, index }) => (
                <Box key={`${grupo.llave}-${row.peaje}`}>
                  <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                    {row.peaje}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="Cantidad de pasos"
                      required={porPeaje}
                      fullWidth
                      disabled={disabled || (porPeaje && Boolean(reportados[row.peaje]))}
                      value={formatted(row.steps)}
                      onChange={(event) => change(index, "steps", event.target.value)}
                      inputProps={{ inputMode: "numeric" }}
                    />
                    <TextField
                      label="Valor total (COP)"
                      required={porPeaje || row.steps !== ""}
                      fullWidth
                      disabled={disabled || (porPeaje && Boolean(reportados[row.peaje]))}
                      value={formatted(row.value)}
                      onChange={(event) => change(index, "value", event.target.value)}
                      inputProps={{ inputMode: "decimal" }}
                      helperText="Ejemplo: 1.500.000,50"
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      {!porPeaje && total?.value_concil != null && (
        <Alert severity="info">
          Total:{" "}
          {total.steps == null
            ? "Sin pasos reportados"
            : `${Number(total.steps).toLocaleString("es-CO")} pasos`}{" "}
          ·{" "}
          {Number(total.value_concil).toLocaleString("es-CO", {
            style: "currency",
            currency: "COP",
          })}
        </Alert>
      )}
    </Stack>
  );
}
ConcessionPeajes.propTypes = {
  detailReference: PropTypes.string,
  code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  conciliationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  concesion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  keyConcil: PropTypes.string,
  rows: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  porPeaje: PropTypes.bool,
  onEnviarPeaje: PropTypes.func,
  reportados: PropTypes.object,
};
ConcessionPeajes.defaultProps = {
  conciliationId: null,
  concesion: null,
  keyConcil: "",
  disabled: false,
};
